import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { CalendarBlank } from "components/icons/CalendarBlank";
import { Mail } from "components/icons/Mail";
import { PhoneCallIcon } from "components/icons/PhoneCallIcon";
import { WhatsApp } from "components/icons/WhatsApp";
import { QuestionWithCircle } from "components/icons/QuestionWithCircle";
import { RightArrow } from "components/icons/RightArrow";
import { PHONE } from "constants/help";
import { BOOK_A_CALL } from "constants/onBoarding";
import { SALES_EMAIL } from "constants/base";
import { ANAND_PHONE } from "constants/meetingRequestUrls";
import { PlayIconOutline } from "components/icons/PlayIconOutline";
import { PlatformTourPlayerModal } from "components/PlatformTour/PlatformTourPlayerModal";
import { PLATFORM_TOUR_WATCH_CLICKED } from "constants/analyticsEvents";
import { PLATFORM_TOUR_SOURCE } from "constants/productTour";
import { useAnalytics } from "hooks/useAnalytics";
import { useModal } from "hooks/useModal";

type HelpMenuItem = {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  description: string;
  onSelect: () => void;
};

const HelpMenuItemIcon = ({
  icon,
  iconBg,
}: {
  icon: React.ReactNode;
  iconBg: string;
}) => (
  <div
    className={`t-flex t-h-9 t-w-9 t-shrink-0 t-items-center t-justify-center t-rounded-lg ${iconBg}`}
  >
    {icon}
  </div>
);

export const NeedHelp = () => {
  const tourPlayer = useModal();
  const { trackEvent } = useAnalytics();

  const handleWatchTour = () => {
    trackEvent(PLATFORM_TOUR_WATCH_CLICKED, {
      source: PLATFORM_TOUR_SOURCE.NEED_HELP,
    });
    tourPlayer.open();
  };

  const menuItems: HelpMenuItem[] = [
    {
      icon: <CalendarBlank className="t-size-4 t-text-purple-50" />,
      iconBg: "t-bg-purple-0",
      title: "Book a demo",
      description:
        "Book a meeting with a specialist and understand the benefits that Inkle offers",
      onSelect: () => window.open(BOOK_A_CALL, "_blank", "noopener,noreferrer"),
    },
    {
      icon: <PlayIconOutline className="t-size-4 !t-text-purple-50" />,
      iconBg: "t-bg-purple-0",
      title: "View platform tour",
      description:
        "Take a quick tour of Inkle and see how we handle your tax filings and compliance.",
      onSelect: handleWatchTour,
    },
    {
      icon: <Mail className="t-text-purple-50" />,
      iconBg: "t-bg-purple-0",
      title: "Get in touch",
      description: "Drop your query and we'll get back to as soon as possible",
      onSelect: () =>
        window.open(`mailto:${SALES_EMAIL}`, "_blank", "noopener,noreferrer"),
    },
    {
      icon: <PhoneCallIcon />,
      iconBg: "t-bg-purple-0",
      title: "Call us",
      description: "Reach us directly for personalized guidance",
      onSelect: () =>
        window.open(`tel:${ANAND_PHONE}`, "_blank", "noopener,noreferrer"),
    },
    {
      icon: <WhatsApp className="t-text-[#25D366]" width={16} height={16} />,
      iconBg: "t-bg-[#25D366]/10",
      title: "WhatsApp",
      description: "Chat with our team on WhatsApp",
      onSelect: () =>
        window.open(`https://wa.me/${PHONE}`, "_blank", "noopener,noreferrer"),
    },
  ];

  return (
    <>
      <PlatformTourPlayerModal
        open={tourPlayer.isOpen}
        onClose={tourPlayer.close}
      />
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <button
            type="button"
            className="all:unset t-fixed t-bottom-6 t-right-8 t-z-10 t-flex t-cursor-pointer t-items-center t-gap-1.5 t-rounded-full t-border t-border-solid t-border-neutral-10 t-bg-white t-px-4 t-py-2.5 t-shadow-light-30 t-transition-shadow t-duration-200 hover:t-shadow-md"
          >
            <span className="t-text-text-30 -t-mt-1">
              <QuestionWithCircle />
            </span>
            <span className="t-text-body-sm t-font-medium t-text-text-60">
              Need help?
            </span>
          </button>
        </Dropdown.Trigger>

        <Dropdown.Portal>
          <Dropdown.Content
            side="top"
            align="end"
            sideOffset={12}
            className="!t-w-80 !t-rounded-xl !t-p-0"
          >
            <Dropdown.Label className="t-border-0 t-border-b t-border-solid t-border-neutral-10 !t-px-4 !t-py-3">
              Need help?
            </Dropdown.Label>
            <div className="t-p-2">
              {menuItems.map((item) => (
                <Dropdown.Item
                  key={item.title}
                  onSelect={item.onSelect}
                  icon={
                    <HelpMenuItemIcon icon={item.icon} iconBg={item.iconBg} />
                  }
                  tag={<RightArrow />}
                  className="!t-items-start !t-gap-3 !t-overflow-visible !t-whitespace-normal !t-py-3"
                >
                  <div>
                    <div className="t-text-subtext t-text-text-100">
                      {item.title}
                    </div>
                    <div className="t-text-body-sm t-text-text-30">
                      {item.description}
                    </div>
                  </div>
                </Dropdown.Item>
              ))}
            </div>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </>
  );
};
